'use client';

import { useParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { useSettings } from 'src/common/hooks/useSettings';
import { LayoutType } from 'src/common/types/layout';
import { withProjectsAuthGuard } from 'src/modules/auth/hocs/with-projects-auth-guard';
import { ProjectsProvider } from 'src/modules/projects/context';
import useProjectIntegrationHealth from 'src/modules/projects/hooks/useProjectIntegrationHealth';

import Seo from '@/components/Seo';
import { useMediaQuery } from '@/design-system/hooks/useMediaQuery';

import { useProjectsSections } from '../navigation/config';
import { MobileNav } from '../navigation/mobile-nav';
import { VerticalLayoutContainer, VerticalLayoutRoot } from '../navigation/vertical-layout';
import { SideNav } from '../navigation/vertical-layout/side-nav';
import { TopNav } from '../navigation/vertical-layout/top-nav';
import { useMobileNav } from '../navigation/vertical-layout/use-mobile-nav';

interface LayoutProps {
  children?: ReactNode;
}

const ProjectsLayoutRoot = (props: LayoutProps) => {
  const { children } = props;
  const integrationHealth = useProjectIntegrationHealth();
  const lgUp = useMediaQuery('(min-width: 1200px)');
  const mobileNav = useMobileNav();
  const params = useParams();
  const sections = useProjectsSections();
  const settings = useSettings();

  return (
    <>
      <Seo title={params?.slug as string} />
      <>
        <TopNav onMobileNavOpen={mobileNav.handleOpen} />
        {lgUp && <SideNav color={settings.navColor} sections={sections} layoutType={LayoutType.PROJECT} {...props} />}
        {!lgUp && (
          <MobileNav
            color={settings.navColor}
            onClose={mobileNav.handleClose}
            open={mobileNav.open}
            sections={sections}
            layoutType={LayoutType.PROJECT}
            {...props}
          />
        )}
        <VerticalLayoutRoot>
          <VerticalLayoutContainer>
            <>
              <div className="tw:container tw:max-w-[1440px] tw:mx-auto tw:px-6">{integrationHealth}</div>
              {children}
            </>
          </VerticalLayoutContainer>
        </VerticalLayoutRoot>
      </>
    </>
  );
};

export const ProjectsLayout = withProjectsAuthGuard((props: LayoutProps) => {
  return (
    <ProjectsProvider>
      <ProjectsLayoutRoot {...props} />
    </ProjectsProvider>
  );
});
